import type { Icons } from '@/components/icon';
import type { FilterableColumn } from '@/components/table/filter-bar';
import type { FilterCondition } from '@/components/table/filter-chip';
import type { SortColumn } from '@/components/table/sort-button';
import type { components } from '@/services/api-allo/api-allo-v1';
import type { ColumnDef } from '@tanstack/react-table';

// ============================================================================
// Types
// ============================================================================

export type TranslationFn = (key: string) => string;

export type TableDefinition = components['schemas']['TableDefinition'];
export type TableColumnDefinition = components['schemas']['TableColumnDefinition'];
export type TableFilterDefinition = components['schemas']['TableFilterDefinition'];
export type ApiFilterCondition = TableFilterDefinition['condition'];
export type ColumnType = TableColumnDefinition['type'];

export type ColumnIconConfig = Record<string, keyof typeof Icons>;
export type TranslationKeyConfig = Record<string, string>;

export type CellBuilderOptions<TData> = {
  column: TableColumnDefinition;
  t: TranslationFn;
  iconConfig: ColumnIconConfig;
  translationConfig: TranslationKeyConfig;
  handlers?: Record<string, unknown>;
  state?: Record<string, unknown>;
  _phantom?: TData;
};

export type CustomCellConfig<TData> = {
  key: string;
  builder: (options: CellBuilderOptions<TData>) => ColumnDef<TData, unknown>;
};

export type BuildColumnsOptions<TData> = {
  t: TranslationFn;
  iconConfig: ColumnIconConfig;
  translationConfig: TranslationKeyConfig;
  customCells: CustomCellConfig<TData>[];
  handlers?: Record<string, unknown>;
  state?: Record<string, unknown>;
};

// ============================================================================
// Utils
// ============================================================================

export function mapApiConditionToUi(
  condition: ApiFilterCondition | (string & {}),
): FilterCondition {
  switch (condition) {
    case 'STARTS_WITH':
      return 'starts_with';
    case 'IS':
    case 'EQUALS':
      return 'is';
    case 'CONTAINS':
      return 'contains';
    case 'NOT_CONTAINS':
      return 'not_contains';
    case 'ENDS_WITH':
      return 'ends_with';
    case 'IS_NOT':
    case 'NOT_EQUALS':
      return 'is_not';
    case 'EMPTY':
      return 'empty';
    case 'NOT_EMPTY':
      return 'not_empty';
    case 'GREATER_THAN':
      return 'greater_than';
    case 'GREATER_THAN_OR_EQUAL':
      return 'greater_than_or_equal';
    case 'LESS_THAN':
      return 'less_than';
    case 'LESS_THAN_OR_EQUAL':
      return 'less_than_or_equal';
    case 'FROM':
    case 'TO':
      return 'is';
    case 'BEFORE':
      return 'before';
    case 'AFTER':
      return 'after';
    case 'DATE_EQUALS':
      return 'date_equals';
    case 'HAS':
      return 'has';
    default:
      return 'is';
  }
}

export function conditionRequiresValue(condition: FilterCondition): boolean {
  return condition !== 'empty' && condition !== 'not_empty';
}

export function getIconForColumnType(
  type: ColumnType,
  options?: { hasOptions?: boolean },
): keyof typeof Icons {
  if (options?.hasOptions) {
    return 'ListBullet';
  }
  switch (type) {
    case 'TEXT':
      return 'TextAlignLeft';
    case 'PHONE':
      return 'Phone';
    case 'EMAIL':
      return 'At';
    case 'WEBSITE':
      return 'Globe';
    case 'NUMBER':
      return 'Hash';
    case 'BOOLEAN':
      return 'Boolean';
    case 'DATE':
      return 'Calendar';
    case 'ACTIVITY':
      return 'Pulse';
    default:
      return 'TextAlignLeft';
  }
}

/**
 * Build a dynamic column definition from an API column.
 * Used for custom/synced fields that don't have hardcoded renderers.
 */
export function buildDynamicColumn<TData>(
  apiColumn: TableColumnDefinition,
): ColumnDef<TData, unknown> {
  const { key, label, type } = apiColumn;

  return {
    id: key,
    accessorFn: (row: TData) => {
      const r = row as Record<string, unknown>;
      return r[key] ?? (r.custom_properties as Record<string, unknown> | undefined)?.[key];
    },
    header: label ?? key,
    meta: {
      icon: getIconForColumnType(type, {
        hasOptions: (apiColumn.options?.length ?? 0) > 0,
      }) as keyof typeof Icons,
      editable: type === 'TEXT' || type === 'NUMBER',
    },
  } as ColumnDef<TData, unknown>;
}

// ============================================================================
// Builders
// ============================================================================

export function buildFilterableColumns(
  columns: TableColumnDefinition[],
  t: (key: string) => string,
  iconConfig: ColumnIconConfig,
  translationConfig: TranslationKeyConfig,
): FilterableColumn[] {
  return columns
    .filter((col) => col.filters && col.filters.length > 0)
    .map((col) => {
      const key = col.key;
      const translationKey = translationConfig[key];
      const hasOptions = (col.options?.length ?? 0) > 0;
      const iconName =
        (iconConfig[key] as keyof typeof Icons | undefined) ??
        getIconForColumnType(col.type, { hasOptions });

      // Prioritize API label, then translation, then key as fallback
      const label = col.label ?? (translationKey ? t(translationKey) : key);

      // Map API filter conditions to UI conditions (deduplicated)
      const conditions = col.filters
        ?.map((f) => mapApiConditionToUi(f.condition))
        .filter((v, i, a) => a.indexOf(v) === i);

      return {
        value: key,
        label,
        icon: iconName,
        conditions,
        inputType: col.type === 'NUMBER' ? 'number' : undefined,
      };
    });
}

export function buildSortableColumns(
  columns: TableColumnDefinition[],
  t: (key: string) => string,
  iconConfig: ColumnIconConfig,
  translationConfig: TranslationKeyConfig,
): SortColumn[] {
  return columns
    .filter((col) => col.sortable)
    .map((col) => {
      const key = col.key;
      const translationKey = translationConfig[key];
      const hasOptions = (col.options?.length ?? 0) > 0;
      const iconName =
        (iconConfig[key] as keyof typeof Icons | undefined) ??
        getIconForColumnType(col.type, { hasOptions });

      // Prioritize API label, then translation, then key as fallback
      const label = col.label ?? (translationKey ? t(translationKey) : key);

      return {
        value: key,
        label,
        icon: iconName,
      };
    });
}

export function buildTableColumns<TData>(
  columns: TableColumnDefinition[],
  options: BuildColumnsOptions<TData>,
): ColumnDef<TData, unknown>[] {
  const { t, iconConfig, translationConfig, customCells, handlers, state } = options;

  const customCellMap = new Map<string, CustomCellConfig<TData>>();
  for (const cell of customCells) {
    customCellMap.set(cell.key, cell);
  }

  return columns
    .filter((col) => col.is_visible !== false && !col.hide_column)
    .map((col) => {
      const key = col.key;
      const customCell = customCellMap.get(key);

      if (customCell) {
        return customCell.builder({
          column: col,
          t,
          iconConfig,
          translationConfig,
          handlers,
          state,
        });
      }

      const translationKey = translationConfig[key];
      const hasOptions = (col.options?.length ?? 0) > 0;
      const iconName = (iconConfig[key] ??
        getIconForColumnType(col.type, { hasOptions })) as keyof typeof Icons;

      // Prioritize API label, then translation, then key as fallback
      const header = col.label ?? (translationKey ? t(translationKey) : key);

      return {
        id: key,
        accessorKey: key,
        header,
        meta: {
          icon: iconName,
        },
      } as ColumnDef<TData, unknown>;
    });
}
